import {useCallback, useEffect, useRef, useState} from 'react';
import {useBlocker, useNavigate} from 'react-router-dom';

type BlockedLocation = {
    pathname: string;
    state?: unknown;
};

// Dialog-agnostic: blocks in-app navigation while `shouldBlock` is true, and
// only actually navigates once `confirmNavigation` is called (never via
// blocker.proceed(), which has previously proven unreliable here).
export const useNavigationBlocker = (shouldBlock: boolean) => {
    const navigate = useNavigate();
    const location = useRef<BlockedLocation>();
    const [isBlocked, setIsBlocked] = useState(false);

    const blocker = useBlocker(({currentLocation, nextLocation}) => {
        if (currentLocation.pathname !== nextLocation.pathname && !isBlocked) {
            location.current = nextLocation;

            return shouldBlock;
        }

        return false;
    });

    useEffect(() => {
        if (
            (blocker && blocker.state === 'blocked') ||
            blocker.state === 'proceeding'
        ) {
            setIsBlocked(true);
        }
    }, [blocker]);

    const confirmNavigation = useCallback(() => {
        setIsBlocked(false);

        if (typeof location.current?.pathname === 'string') {
            const state =
                'state' in location.current
                    ? {state: location.current.state}
                    : {};

            void navigate(location.current, state);
        }
    }, [navigate]);

    const cancelNavigation = useCallback(() => {
        setIsBlocked(false);
    }, []);

    return {isBlocked, confirmNavigation, cancelNavigation};
};
